import { useState } from 'react';
import { Copy, Check, Users, ArrowUpRight } from 'lucide-react';
import { TenureChip } from '../TenureBadge/TenureBadge';
import { telUrl, mailtoUrl } from '../../utils/deepLinks';
import { parseHireDate } from '../../utils/hireDate';

/**
 * Persistent profile rail.
 *
 * Contact details lived inside the Overview tab and the reporting line inside the
 * Team tab, so the two things people open a profile *for* both disappeared the
 * moment they looked at anything else. The rail keeps them on screen whichever
 * section is active.
 *
 * Copy buttons because the most common action on a phone number or address in a
 * directory is copying it, and selecting text out of a modal is fiddly.
 */

function CopyButton({ value, label }) {
  const [copied, setCopied] = useState(false);

  const copy = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch {
      // Clipboard can be blocked by permissions policy; the value is still
      // selectable, so failing quietly is better than an error toast here.
    }
  };

  return (
    <button
      onClick={copy}
      title={copied ? 'Copied' : `Copy ${label}`}
      aria-label={copied ? 'Copied' : `Copy ${label}`}
      className={`flex-none p-1 rounded-md transition-colors ${copied ? 'text-accent' : 'text-ink-4 hover:text-ink-2'}`}
    >
      {copied ? <Check size={11} /> : <Copy size={11} />}
    </button>
  );
}

/**
 * `.field` — an 88px label column, the value, and the copy affordance, on a shared
 * baseline with a dashed rule between rows.
 *
 * The label used to sit stacked above its value in 9px type with an icon beside it,
 * so five contact fields were fifteen lines of text at three sizes and none of the
 * values lined up with each other. A dt/dd grid is also the honest markup: these
 * are term/definition pairs.
 */
function Row({ label, value, href, copyable = true, dashed = true }) {
  if (!value) return null;
  return (
    <div
      className={`group/row grid grid-cols-[76px_minmax(0,1fr)_auto] gap-2.5 items-baseline py-1.5
                  ${dashed ? 'border-b border-dashed border-line last:border-b-0' : ''}`}
    >
      <dt className="text-[10px] font-bold uppercase tracking-[0.07em] text-ink-3">{label}</dt>
      <dd className="m-0 min-w-0 text-[12.5px] text-ink-2 truncate" title={value}>
        {href ? (
          <a href={href} className="text-accent no-underline hover:underline">{value}</a>
        ) : value}
      </dd>
      {copyable ? (
        // Hidden until hover, but focus-visible has to bring it back or it is
        // unreachable by keyboard.
        <span className="opacity-0 group-hover/row:opacity-100 focus-within:opacity-100 transition-opacity">
          <CopyButton value={value} label={label} />
        </span>
      ) : <span />}
    </div>
  );
}

export function ProfileRail({
  employee,
  manager,
  directReports = [],
  onSelectEmployee,
  onViewInOrgChart,
}) {
  if (!employee) return null;

  const joined = parseHireDate(employee.hireDate)?.toLocaleDateString(undefined, { year: 'numeric', month: 'long' }) ?? null;

  const visibleReports = directReports.slice(0, 3);
  const hiddenReports = directReports.length - visibleReports.length;

  return (
    <aside
      aria-label="Contact and reporting line"
      className="hidden min-[900px]:flex flex-col gap-4 w-full sticky top-0
                 rounded-card border border-line bg-surface shadow-e1 p-4"
    >
      <div>
        <p className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-ink-4 mb-2.5">Contact</p>
        <dl className="m-0">
          <Row label="Email" value={employee.mail} href={mailtoUrl(employee.mail)} />
          <Row label="Mobile" value={employee.mobilePhone} href={telUrl(employee.mobilePhone)} />
          <Row label="Desk" value={employee.businessPhones?.[0]} href={telUrl(employee.businessPhones?.[0])} />
          <Row label="Office" value={employee.officeLocation} />
          <Row label="Joined" value={joined} copyable={false} dashed={false} />
        </dl>
      </div>

      {employee.hireDate && (
        <div>
          <p className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-ink-4 mb-2">Tenure</p>
          <TenureChip hireDate={employee.hireDate} />
        </div>
      )}

      {(manager || directReports.length > 0) && (
        <div>
          <p className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-ink-4 mb-2">Reporting line</p>

          {manager && (
            <button
              onClick={() => onSelectEmployee?.(manager)}
              className="flex items-center gap-2 w-full text-left mb-2 group/mgr"
            >
              <ArrowUpRight size={12} className="flex-none text-ink-4" />
              <span className="min-w-0">
                <span className="block text-[9px] font-bold uppercase tracking-[0.1em] text-ink-4 leading-none">
                  Reports to
                </span>
                <span className="block text-[12px] text-ink-2 truncate mt-0.5 group-hover/mgr:text-accent transition-colors">
                  {manager.displayName}
                </span>
              </span>
            </button>
          )}

          {directReports.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <Users size={12} className="flex-none text-ink-4" />
                <span className="text-[9px] font-bold uppercase tracking-[0.1em] text-ink-4">
                  {directReports.length} direct {directReports.length === 1 ? 'report' : 'reports'}
                </span>
              </div>
              <ul className="space-y-1 pl-[20px]">
                {visibleReports.map((report) => (
                  <li key={report.id}>
                    <button
                      onClick={() => onSelectEmployee?.(report)}
                      className="text-[12px] text-ink-2 hover:text-accent truncate block w-full text-left transition-colors"
                    >
                      {report.displayName}
                    </button>
                  </li>
                ))}
              </ul>
              {/* "show all N" rather than a bare "+5 more", which gave no idea
                  what the rest were or how to reach them. */}
              {hiddenReports > 0 && onViewInOrgChart && (
                <button
                  onClick={() => onViewInOrgChart(employee)}
                  className="mt-1.5 ml-[20px] text-[11px] font-semibold text-accent hover:underline"
                >
                  Show all {directReports.length} in org chart
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {onViewInOrgChart && (
        <button
          onClick={() => onViewInOrgChart(employee)}
          className="mt-auto h-8 rounded-ctl border border-line bg-surface text-[11.5px] font-semibold
                     text-ink-2 hover:text-accent hover:border-line-strong transition-colors"
        >
          View in org chart
        </button>
      )}
    </aside>
  );
}
